import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorMessage, useApi } from '../../api';
import { Alert, Field } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useSyndicate } from '../../session';

export const OFFICER_TITLES = ['president', 'vice_president', 'secretary', 'treasurer', 'director'] as const;
export type OfficerTitle = (typeof OFFICER_TITLES)[number];
export interface Officer {
  id: string;
  userId: string;
  name: string | null;
  title: OfficerTitle;
  startsOn: string;
  endsOn: string | null;
  note: string;
}

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
/** A mandate in force today. */
export const current = (o: Officer, day = today()) => o.startsOn <= day && (!o.endsOn || o.endsOn >= day);

export function useOfficers() {
  const s = useSyndicate();
  const api = useApi();
  return useQuery({ queryKey: [s.tenantId, 'members', 'officers'], queryFn: () => api<Officer[]>(`${s.api}/members/officers`) });
}

function useOfficerMutation<Input>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<unknown>) {
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: (input: Input) => request(api, s.api, input), onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'members', 'officers'] }) });
}

/** The board today: who holds which title, until when; mandates ending within 90 days are flagged. */
export function BoardComposition() {
  const { t, date } = useI18n();
  const b = t.board;
  const officers = useOfficers();
  const day = today();
  const soon = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
  const now = (officers.data ?? []).filter((o) => current(o, day)).sort((x, y) => OFFICER_TITLES.indexOf(x.title) - OFFICER_TITLES.indexOf(y.title));
  if (!officers.data || now.length === 0) return null;
  return (
    <section className="panel">
      <h2>{b.composition}</h2>
      <ul className="compact-list">
        {now.map((o) => (
          <li key={o.id}>
            <span>
              <strong>{b.titles[o.title]}</strong> — {o.name ?? '—'}
            </span>
            <span className="muted small">
              {o.endsOn ? b.until(date(o.endsOn)) : b.noEnd}
              {o.endsOn && o.endsOn <= soon && <span className="badge badge-urgent"> {b.endingSoon}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** On a director's page: their titles and terms, and (for directors) appointing, editing or ending them. */
export function OfficerTerms({ userId, name }: { userId: string; name: string }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const b = t.board;
  const officers = useOfficers();
  const mine = (officers.data ?? []).filter((o) => o.userId === userId);
  const editable = s.can('members.roles');
  const [values, setValues] = useState({ title: 'director' as OfficerTitle, startsOn: today(), endsOn: '', note: '' });
  const appoint = useOfficerMutation<typeof values>((api, base, v) => api(`${base}/members/officers`, { method: 'POST', body: { userId, ...v, endsOn: v.endsOn || null } }));
  const end = useOfficerMutation<{ id: string; endsOn: string }>((api, base, v) => api(`${base}/members/officers/${v.id}`, { method: 'PATCH', body: { endsOn: v.endsOn } }));
  const remove = useOfficerMutation<string>((api, base, id) => api(`${base}/members/officers/${id}`, { method: 'DELETE' }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    appoint.mutate(values, { onSuccess: () => setValues((v) => ({ ...v, endsOn: '', note: '' })) });
  };
  const error = appoint.error ?? end.error ?? remove.error;

  return (
    <>
      <h3>{b.titlesHeading}</h3>
      {mine.length === 0 && <p className="muted small">{b.noTitle}</p>}
      <ul className="compact-list">
        {mine.map((o) => (
          <li key={o.id}>
            <span>
              <strong>{b.titles[o.title]}</strong> {current(o) ? '' : <span className="muted">({b.past})</span>}
              {o.note && <span className="muted"> · {o.note}</span>}
            </span>
            <span className="muted small">
              {date(o.startsOn)} → {o.endsOn ? date(o.endsOn) : '…'}
            </span>
            {editable && (
              <span>
                {current(o) && (
                  <button type="button" className="link-button small" onClick={() => window.confirm(b.confirmEnd(b.titles[o.title], name)) && end.mutate({ id: o.id, endsOn: today() })}>
                    {b.endToday}
                  </button>
                )}{' '}
                <button type="button" className="link-button small" onClick={() => window.confirm(b.confirmDelete) && remove.mutate(o.id)}>
                  {b.delete}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <form className="form" onSubmit={submit}>
          <div className="row">
            <Field label={b.title}>
              <select value={values.title} onChange={set('title')}>
                {OFFICER_TITLES.map((x) => (
                  <option key={x} value={x}>
                    {b.titles[x]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={b.startsOn}>
              <input type="date" value={values.startsOn} onChange={set('startsOn')} required />
            </Field>
            <Field label={b.endsOn} hint={b.endsOnHint}>
              <input type="date" value={values.endsOn} onChange={set('endsOn')} />
            </Field>
          </div>
          <Field label={b.note} hint={t.common.optional}>
            <input value={values.note} onChange={set('note')} placeholder={b.notePlaceholder} />
          </Field>
          <div className="form-actions">
            <button type="submit" className="button button-small button-primary" disabled={appoint.isPending}>
              {b.appoint}
            </button>
          </div>
        </form>
      )}
      {error && <Alert>{errorMessage(error, t)}</Alert>}
    </>
  );
}
